import { useState, useCallback, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertCircle, XCircle, Loader2, Sparkles, Microscope, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

const VERDICT_STYLE = {
  supported:   { icon: CheckCircle2, cls: 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:border-emerald-900 dark:text-emerald-200', label: 'Supported' },
  partial:     { icon: AlertCircle,  cls: 'border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:border-amber-900 dark:text-amber-200',          label: 'Partial' },
  unsupported: { icon: XCircle,      cls: 'border-red-300 bg-red-50 text-red-800 dark:bg-red-950/30 dark:border-red-900 dark:text-red-200',                       label: 'Unsupported' },
  code_supported: { icon: CheckCircle2, cls: 'border-sky-300 bg-sky-50 text-sky-900 dark:bg-sky-950/30 dark:border-sky-900 dark:text-sky-200',                     label: 'Verified in code' },
};

/**
 * ClaimGroundingDialog — Sprint 4 (PaperQA2-style).
 *
 * Extracts atomic claims from the chapter, retrieves chunks per claim,
 * and produces a per-claim verdict (supported / partial / unsupported) with
 * supporting excerpts and suggested searches for gaps.
 */
export default function ClaimGroundingDialog({
  open, onOpenChange,
  userId, chapter, language = 'en',
  onRequestSelfFeedback,
}) {
  const [phase, setPhase] = useState('idle');   // idle | running | done
  const [report, setReport] = useState(null);
  const [feedbackStarting, setFeedbackStarting] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const poll = useCallback((id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const r = await thesisApi.getGroundingReport(userId, id);
        if (r.status === 'ready') {
          clearInterval(pollRef.current); pollRef.current = null;
          setReport(r);
          setPhase('done');
          const u = r.stats?.unsupported || 0;
          toast.success(`Found ${r.stats?.total || 0} claims · ${u} need additional evidence`);
        } else if (r.status === 'failed') {
          clearInterval(pollRef.current); pollRef.current = null;
          toast.error(`Claim grounding failed: ${r.error || 'Unknown error'}`);
          setPhase('idle');
        }
      } catch (e) {
        if (e?.response?.status === 404) {
          clearInterval(pollRef.current); pollRef.current = null;
          toast.error('Report not found');
          setPhase('idle');
        }
      }
    };
    pollRef.current = setInterval(tick, 5000);
    tick();
  }, [userId]);

  const run = useCallback(async () => {
    if (!chapter?.id) return;
    setPhase('running');
    setReport(null);
    try {
      const res = await thesisApi.claimGrounding(userId, chapter.id, language);
      poll(res.grounding_id);
    } catch (e) {
      toast.error(`Claim grounding failed to start: ${e?.response?.data?.detail || e.message}`);
      setPhase('idle');
    }
  }, [userId, chapter, language, poll]);

  const startSelfFeedback = useCallback(async () => {
    if (!chapter?.id) return;
    setFeedbackStarting(true);
    try {
      await thesisApi.selfFeedbackRefine(userId, chapter.id, language);
      toast.success('Self-feedback refinement started. The chapter will update when ready (poll status).');
      onOpenChange(false);
      onRequestSelfFeedback?.();
    } catch (e) {
      toast.error(`Self-feedback failed to start: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setFeedbackStarting(false);
    }
  }, [userId, chapter, language, onOpenChange, onRequestSelfFeedback]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (phase !== 'running') onOpenChange(v); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="claim-grounding-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-serif">
            <Microscope className="h-5 w-5 text-indigo-600" />
            Claim Grounding · PaperQA2-style
          </DialogTitle>
          <DialogDescription>
            Extract every atomic claim from your chapter and verify it against the project library. Each claim gets a supported / partial / unsupported verdict with supporting excerpts.
          </DialogDescription>
        </DialogHeader>

        {phase === 'idle' && (
          <div className="space-y-4">
            <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 dark:bg-indigo-950/20 dark:border-indigo-900 p-4">
              <p className="text-sm text-slate-800 dark:text-slate-200">
                <strong>Chapter:</strong> {chapter?.title || '—'} · {((chapter?.markdown || '').split(/\s+/).length).toLocaleString()} words
              </p>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-2">
                We will: (1) extract up to 15 atomic scientific claims · (2) retrieve top-6 chunks per claim · (3) ask GPT-5.2 to judge whether the chunks support each claim · (4) suggest a search query for unsupported claims.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="grounding-cancel">Cancel</Button>
              <Button onClick={run} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="grounding-run-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Ground claims
              </Button>
            </div>
          </div>
        )}

        {phase === 'running' && (
          <div className="py-16 text-center">
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-indigo-600" />
            <p className="mt-4 text-sm font-medium">Extracting claims and verifying each against the library…</p>
            <p className="mt-1 text-xs text-slate-500">Each claim spawns its own retrieval + LLM judge call. 1–3 minutes typical. We poll every 5 seconds.</p>
          </div>
        )}

        {phase === 'done' && report && (
          <div className="space-y-3">
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
              <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2">
                <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Total</p>
                <p className="text-2xl font-bold">{report.stats?.total || 0}</p>
              </div>
              <div className="rounded-md border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 p-2">
                <p className="text-xs uppercase tracking-widest text-emerald-700 dark:text-emerald-300 font-semibold">Supported</p>
                <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{report.stats?.supported || 0}</p>
              </div>
              <div className="rounded-md border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 p-2">
                <p className="text-xs uppercase tracking-widest text-amber-700 dark:text-amber-300 font-semibold">Partial</p>
                <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{report.stats?.partial || 0}</p>
              </div>
              <div className="rounded-md border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 p-2">
                <p className="text-xs uppercase tracking-widest text-red-700 dark:text-red-300 font-semibold">Unsupported</p>
                <p className="text-2xl font-bold text-red-700 dark:text-red-300">{report.stats?.unsupported || 0}</p>
              </div>
              <div className="rounded-md border border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/30 p-2" data-testid="grounding-code-supported-tile">
                <p className="text-xs uppercase tracking-widest text-sky-700 dark:text-sky-300 font-semibold">In code</p>
                <p className="text-2xl font-bold text-sky-700 dark:text-sky-300">{report.stats?.code_supported || 0}</p>
              </div>
            </div>

            {(report.stats?.unsupported || 0) + (report.stats?.partial || 0) > 0 && (
              <div className="rounded-lg border border-indigo-300 bg-indigo-50 dark:bg-indigo-950/30 dark:border-indigo-900 p-3 flex items-start gap-3">
                <RefreshCcw className="h-4 w-4 text-indigo-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-200">Refine with self-feedback?</p>
                  <p className="text-xs text-indigo-800 dark:text-indigo-300 mt-0.5">OpenScholar-style: critique → re-retrieve evidence for the gaps → refine the draft.</p>
                </div>
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={startSelfFeedback} disabled={feedbackStarting} data-testid="grounding-start-feedback-btn">
                  {feedbackStarting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Start refinement'}
                </Button>
              </div>
            )}

            {/* Claims list */}
            <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1" data-testid="grounding-claims-list">
              {(report.claims || []).map((c) => {
                const v = VERDICT_STYLE[c.verdict] || VERDICT_STYLE.partial;
                const VerdictIcon = v.icon;
                return (
                  <div key={c.claim_id} className={`rounded-md border px-3 py-2.5 ${v.cls}`} data-testid={`grounding-claim-${c.claim_id}`}>
                    <div className="flex items-start gap-2">
                      <VerdictIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-xs font-bold uppercase tracking-widest opacity-70">{v.label} · #{c.claim_id}</span>
                          <span className="text-[10px] opacity-60">{c.section_hint}</span>
                        </div>
                        <p className="text-sm leading-relaxed text-slate-900 dark:text-slate-100">{c.claim_text}</p>
                        {c.rationale && (
                          <p className="text-xs italic mt-1 opacity-80">{c.rationale}</p>
                        )}
                        {c.supporting_chunks?.length > 0 && (
                          <details className="mt-1.5">
                            <summary className="text-[11px] cursor-pointer opacity-75 hover:opacity-100">
                              {c.supporting_chunks.length} supporting excerpt{c.supporting_chunks.length > 1 ? 's' : ''}
                            </summary>
                            <ul className="mt-1 space-y-1">
                              {c.supporting_chunks.map((sc, i) => (
                                <li key={i} className="text-[11px] pl-2 border-l-2 border-current opacity-90">
                                  <strong>{sc.source_title}</strong>
                                  {sc.doi && <span className="text-slate-500"> · {sc.doi}</span>}
                                  <p className="opacity-80 mt-0.5">{sc.excerpt}</p>
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                        {c.suggested_search && (
                          <p className="text-[11px] mt-1.5 opacity-85">
                            <span className="font-semibold">Suggested search:</span> <code>{c.suggested_search}</code>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {!report.claims?.length && (
                <p className="text-sm text-slate-500 italic text-center py-8">No claims extracted (chapter may be too short).</p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-700">
              <Button variant="ghost" onClick={() => setPhase('idle')} data-testid="grounding-rerun">Run again</Button>
              <Button onClick={() => onOpenChange(false)} data-testid="grounding-close">Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
